import { useEffect, useState } from 'react';
import { useMobilePreviewContainer } from '../context/MobilePreviewContext';

export type LayoutMode = 'mobile' | 'desktop';

/**
 * Width (px) at which the layout switches from mobile (bottom chat panel
 * with drag-modes) to desktop (right-side fixed chat sidebar).
 *
 * 1024px = standard tablet-landscape / small-desktop threshold. The
 * canvas above 1024px gets comfortably wider than the 420px sidebar,
 * leaving ≥ 600px for the scene viewport.
 */
export const LAYOUT_DESKTOP_BREAKPOINT = 1024;

const readWindowLayoutMode = (): LayoutMode => {
  if (typeof window === 'undefined') return 'mobile';
  return window.innerWidth >= LAYOUT_DESKTOP_BREAKPOINT ? 'desktop' : 'mobile';
};

/**
 * Reactive layout mode. Switches between `'mobile'` and `'desktop'` based
 * on window width without remounting the React tree. Inside the mobile
 * preview frame (`?viewMode=mobile`) we always return `'mobile'` — the
 * preview-fixed container is mobile-shaped regardless of window size.
 *
 * No debounce: cross-breakpoint flicker is small (one frame), and adding
 * debounce would delay legitimate layout transitions on rotate / resize.
 */
export const useLayoutMode = (): LayoutMode => {
  const previewContainer = useMobilePreviewContainer();
  const [mode, setMode] = useState<LayoutMode>(() =>
    previewContainer ? 'mobile' : readWindowLayoutMode(),
  );

  useEffect(() => {
    if (previewContainer) {
      setMode('mobile');
      return;
    }
    if (typeof window === 'undefined') return;

    const handleResize = () => setMode(readWindowLayoutMode());
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [previewContainer]);

  return mode;
};
